import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Flex,
  Button,
  Text,
  IconButton,
  DropdownMenu,
  Checkbox,
} from '@radix-ui/themes';

import YearNavigation from '../../../../campaigns/components/CampaignsTable/CalendarView/YearNavigation.jsx';
import BroadcastMonthCard from './BroadcastMonthCard.jsx';
import BroadcastMonthDetailView from './BroadcastMonthDetailView.jsx';
import SectionCard from '../../../../../shared/SectionCard/SectionCard.jsx';
import {
  get12MonthRange,
  BROADCAST_STATUS_TYPES,
  BROADCAST_STATUS_CONFIG,
  BROADCAST_DOT_COLORS,
  MONTHS,
} from '../../../../campaigns/components/CampaignsTable/CalendarView/helpers.js';
import { useFetchBroadcastsByYear } from '../../../hooks/useFetchBroadcastsByYear';
import styles from '../../../../campaigns/components/CampaignsTable/CalendarView/CalendarView.module.css';

const BROADCAST_STATUS_ITEMS = [
  {
    id: BROADCAST_STATUS_TYPES.DRAFT,
    label: BROADCAST_STATUS_CONFIG.draft.label,
  },
  {
    id: BROADCAST_STATUS_TYPES.SCHEDULED,
    label: BROADCAST_STATUS_CONFIG.scheduled.label,
  },
  {
    id: BROADCAST_STATUS_TYPES.SENT,
    label: BROADCAST_STATUS_CONFIG.sent.label,
  },
];

export default function BroadcastCalendarView() {
  const { t } = useTranslation('broadcastMessages');
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth();

  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedBroadcastFilter, setSelectedBroadcastFilter] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [hoveredBroadcast, setHoveredBroadcast] = useState(null);

  const { broadcastsByMonth } = useFetchBroadcastsByYear(selectedYear);
  const months = get12MonthRange(selectedYear);

  const handleBroadcastClick = broadcast => {
    if (broadcast.status === 'draft') {
      window.location.href = `/client_admin/broadcast_messages#/edit/${broadcast.currentStep ||
        'review'}/${broadcast.uuid}?from=broadcasts`;
    } else {
      window.location.href = `/client_admin/broadcast_messages#/${broadcast.uuid}?from=broadcasts`;
    }
  };

  const handleBroadcastHover = (uuid, isHovered) => {
    setHoveredBroadcast(isHovered ? uuid : null);
  };

  const handlePreviousYear = () => {
    setSelectedYear(selectedYear - 1);
  };

  const handleNextYear = () => {
    setSelectedYear(selectedYear + 1);
  };

  const handleBroadcastFilterChange = statusId => {
    setSelectedBroadcastFilter(prev => {
      if (prev.includes(statusId)) {
        return prev.filter(id => id !== statusId);
      }
      return [...prev, statusId];
    });
  };

  const handleMonthClick = ({ month, monthIndex }) => {
    setSelectedMonth({ month, monthIndex });
  };

  const handleBackToYear = () => {
    setSelectedMonth(null);
  };

  const handlePreviousMonth = () => {
    if (selectedMonth.monthIndex === 0) {
      setSelectedYear(selectedYear - 1);
      setSelectedMonth({ month: MONTHS[11], monthIndex: 11 });
    } else {
      const newIndex = selectedMonth.monthIndex - 1;
      setSelectedMonth({ month: MONTHS[newIndex], monthIndex: newIndex });
    }
  };

  const handleNextMonth = () => {
    if (selectedMonth.monthIndex === 11) {
      setSelectedYear(selectedYear + 1);
      setSelectedMonth({ month: MONTHS[0], monthIndex: 0 });
    } else {
      const newIndex = selectedMonth.monthIndex + 1;
      setSelectedMonth({ month: MONTHS[newIndex], monthIndex: newIndex });
    }
  };

  return (
    <SectionCard
      contentClassName={styles.calendarContainer}
      headerContent={
        <Flex align="center" justify="between" width="100%">
          {selectedMonth ? (
            <Flex align="center" gap="3">
              <Button variant="ghost" size="2" onClick={handleBackToYear}>
                <i className="fa-regular fa-arrow-left" />
                {t('calendar.backToYear')}
              </Button>
              <Flex className={styles.yearNavigation} align="center">
                <IconButton
                  className={styles.yearNavigationButton}
                  onClick={handlePreviousMonth}
                  aria-label={t('calendar.previousMonth')}
                  variant="soft"
                  color="gray"
                  size="2"
                >
                  <i className="fa-solid fa-chevron-left" />
                </IconButton>
                <Text className={styles.yearDisplay} size="4" weight="bold">
                  {selectedMonth.month} {selectedYear}
                </Text>
                <IconButton
                  className={styles.yearNavigationButton}
                  onClick={handleNextMonth}
                  aria-label={t('calendar.nextMonth')}
                  variant="soft"
                  color="gray"
                  size="2"
                >
                  <i className="fa-solid fa-chevron-right" />
                </IconButton>
              </Flex>
            </Flex>
          ) : (
            <YearNavigation
              selectedYear={selectedYear}
              onPrevious={handlePreviousYear}
              onNext={handleNextYear}
            />
          )}
          <Flex align="center" gap="3">
            <DropdownMenu.Root>
              <DropdownMenu.Trigger>
                <Button variant="outline" size="2">
                  <i className="fa-regular fa-filter" />
                  <Text size="2">{t('filter')}</Text>
                  <i className="fa-solid fa-chevron-down" />
                </Button>
              </DropdownMenu.Trigger>
              <DropdownMenu.Content>
                {BROADCAST_STATUS_ITEMS.map(item => (
                  <DropdownMenu.Item
                    key={item.id}
                    onSelect={e => {
                      e.preventDefault();
                      handleBroadcastFilterChange(item.id);
                    }}
                  >
                    <Flex align="center" gap="2">
                      <Checkbox
                        checked={selectedBroadcastFilter.includes(item.id)}
                      />
                      <span
                        style={{
                          display: 'inline-block',
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          background: BROADCAST_DOT_COLORS[item.id],
                        }}
                      />
                      <Text size="2">{item.label}</Text>
                    </Flex>
                  </DropdownMenu.Item>
                ))}
              </DropdownMenu.Content>
            </DropdownMenu.Root>
          </Flex>
        </Flex>
      }
    >
      {selectedMonth ? (
        <BroadcastMonthDetailView
          monthIndex={selectedMonth.monthIndex}
          year={selectedYear}
          broadcasts={(broadcastsByMonth[selectedMonth.month] || []).filter(
            b =>
              selectedBroadcastFilter.length === 0 ||
              selectedBroadcastFilter.includes(b.status),
          )}
          onBroadcastClick={handleBroadcastClick}
        />
      ) : (
        <Box className={styles.calendarBg}>
          <Box className={styles.calendarWrapper}>
            <Box className={styles.calendarGrid}>
              {months.map(({ month, year, key, monthIndex }) => {
                const isCurrentMonth =
                  year === currentYear && monthIndex === currentMonth;
                const isPastMonth =
                  year < currentYear ||
                  (year === currentYear && monthIndex < currentMonth);

                const monthBroadcasts = broadcastsByMonth[month] || [];
                const filteredBroadcasts =
                  selectedBroadcastFilter.length === 0
                    ? monthBroadcasts
                    : monthBroadcasts.filter(broadcast =>
                        selectedBroadcastFilter.includes(broadcast.status),
                      );

                return (
                  <BroadcastMonthCard
                    key={key}
                    month={month}
                    monthIndex={monthIndex}
                    year={year}
                    broadcasts={filteredBroadcasts}
                    isCurrentMonth={isCurrentMonth}
                    isPastMonth={isPastMonth}
                    zIndex={months.length - monthIndex}
                    hoveredBroadcast={hoveredBroadcast}
                    onBroadcastHover={handleBroadcastHover}
                    onBroadcastClick={handleBroadcastClick}
                    onMonthClick={handleMonthClick}
                  />
                );
              })}
            </Box>
          </Box>
        </Box>
      )}
    </SectionCard>
  );
}
